/**
 * WallBoard — Phase 8.16 public-safe TV projection view.
 *
 * Route: /organizer/events/:slug/wall
 * Auth : organizer JWT (already in localStorage from the organizer's login).
 *
 * Designed to be projected on a big screen at the venue during the event.
 * NEVER shows PII (phone/email/last name/admin notes/business roster).
 * The backend /wall-board endpoint pre-strips everything down to totals +
 * a first-name-only ticker, so this page is a thin renderer.
 *
 * Refresh cadence: 12s (faster than the pit-wall tile because this is the
 * hero screen for the room). Auto full-screen prompt on load.
 */
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, Star, UserPlus, Maximize2 } from "lucide-react";
import { orgApi } from "../lib/organizerApi";

const REFRESH_MS = 12_000;

function formatAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso);
  const s = Math.max(0, Math.floor((Date.now() - then.getTime()) / 1000));
  if (s < 30) return "vừa xong";
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}p`;
  const h = Math.floor(m / 60);
  return `${h}g`;
}

export default function WallBoard() {
  const { slug } = useParams();
  const [board, setBoard] = useState(null);
  const [err, setErr] = useState(null);
  const [flashKey, setFlashKey] = useState(0);
  // Phase 8.17 — auto-fullscreen overlay on first mount. Persists a "prompted"
  // flag in localStorage so once the organizer dismisses (or enters) fullscreen
  // for this slug, the overlay never shows again on this TV browser.
  const [showFullscreenPrompt, setShowFullscreenPrompt] = useState(() => {
    try {
      const key = `nailViet.wallFullscreenPrompted.${window.location.pathname}`;
      return window.localStorage.getItem(key) !== "1";
    } catch (_e) { return false; }
  });
  const seenIdsRef = useRef(new Set());
  const isFirstLoadRef = useRef(true);
  const timerRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await orgApi.get(`/organizer/events/${slug}/wall-board`);
      // New-checkin detection for the flash pulse. NO chime here — the venue
      // has its own PA / MC.
      const currentIds = new Set((data.ticker || []).map((r) => r.id));
      if (isFirstLoadRef.current) {
        seenIdsRef.current = currentIds;
        isFirstLoadRef.current = false;
      } else {
        const anyNew = (data.ticker || []).some((r) => !seenIdsRef.current.has(r.id));
        if (anyNew) setFlashKey((k) => k + 1);
        seenIdsRef.current = currentIds;
      }
      setBoard(data);
      setErr(null);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Không thể tải bảng chiếu.");
    }
  };

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, REFRESH_MS);
    return () => clearInterval(timerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const enterFullscreen = () => {
    const el = document.documentElement;
    if (el.requestFullscreen) el.requestFullscreen().catch(() => {});
    dismissFullscreenPrompt();
  };

  const dismissFullscreenPrompt = () => {
    setShowFullscreenPrompt(false);
    try {
      const key = `nailViet.wallFullscreenPrompted.${window.location.pathname}`;
      window.localStorage.setItem(key, "1");
    } catch (_e) { /* ignore */ }
  };

  if (err) {
    return (
      <div
        data-testid="wall-error"
        className="fixed inset-0 bg-espresso text-cream flex items-center justify-center text-2xl font-serif p-10"
      >
        {err}
      </div>
    );
  }
  if (!board) {
    return (
      <div
        data-testid="wall-loading"
        className="fixed inset-0 bg-espresso text-cream/60 flex items-center justify-center text-xl font-serif"
      >
        Đang chuẩn bị bảng chiếu…
      </div>
    );
  }

  const percent = Math.min(100, Math.max(0, board.percent || 0));

  return (
    <div
      data-testid="wall-board"
      className="fixed inset-0 overflow-hidden text-cream select-none"
      style={{
        background:
          "radial-gradient(1400px 900px at 50% -20%, #16233F 0%, #0B1226 45%, #05070F 100%)",
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {/* Fullscreen toggle — only visible when NOT in fullscreen; hidden by browser once entered */}
      <button
        type="button"
        onClick={enterFullscreen}
        data-testid="wall-fullscreen"
        className="absolute top-6 right-6 z-20 rounded-full bg-white/5 hover:bg-white/10 text-cream/80 hover:text-cream p-3 transition"
        title="Toàn màn hình"
      >
        <Maximize2 className="w-5 h-5" />
      </button>

      {/* Phase 8.17 — Auto-fullscreen prompt on first visit for this browser.
          Dismisses on either "Chiếu toàn màn hình" (enters FS) or the X. */}
      {showFullscreenPrompt && (
        <div
          data-testid="wall-fs-prompt"
          className="absolute inset-0 z-30 bg-espresso/85 backdrop-blur-sm flex items-center justify-center px-8"
        >
          <div className="max-w-lg w-full rounded-3xl bg-black/40 border border-cream/15 p-8 md:p-10 text-center shadow-2xl">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-300/15 border border-amber-300/30 mb-5">
              <Maximize2 className="w-7 h-7 text-amber-200" />
            </div>
            <h2 className="font-serif text-3xl md:text-4xl text-cream mb-3">
              Chiếu lên TV?
            </h2>
            <p className="text-cream/70 text-base md:text-lg mb-8">
              Bấm nút bên dưới để mở toàn màn hình trước khi khách tới. Bảng chỉ hiển thị số liệu và tên gọi — không có SĐT hay email khách.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                type="button"
                onClick={enterFullscreen}
                data-testid="wall-fs-prompt-enter"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-amber-300 text-espresso px-6 py-3 font-semibold hover:bg-amber-200 active:scale-[0.98] transition"
              >
                <Maximize2 className="w-4 h-4" />
                Chiếu toàn màn hình
              </button>
              <button
                type="button"
                onClick={dismissFullscreenPrompt}
                data-testid="wall-fs-prompt-dismiss"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-cream/20 text-cream/80 hover:text-cream hover:bg-white/5 px-6 py-3 transition"
              >
                Để sau
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="h-full flex flex-col px-10 py-8 md:px-16 md:py-12">
        {/* Header row */}
        <header className="flex items-baseline justify-between gap-6 flex-wrap">
          <div>
            <p
              data-testid="wall-event-name"
              className="font-serif text-3xl md:text-4xl leading-none text-cream"
            >
              {board.event_name}
            </p>
            <p className="text-cream/50 text-sm md:text-base mt-2">
              {board.event_date} · {board.location}
            </p>
          </div>
          <div
            data-testid="wall-percent"
            className="font-serif text-4xl md:text-5xl text-amber-200 tabular-nums"
          >
            {percent.toFixed(0)}%
          </div>
        </header>

        {/* Hero counter */}
        <div className="flex-1 grid grid-cols-1 md:grid-cols-[2fr_1fr] gap-10 mt-10 items-center">
          <div
            key={flashKey}
            data-testid="wall-hero"
            className="rounded-3xl bg-black/25 border border-cream/10 px-10 py-10 md:px-16 md:py-12 relative overflow-hidden wall-flash"
          >
            <div className="text-cream/50 text-sm uppercase tracking-[0.28em] font-semibold mb-4">
              Đã check-in
            </div>
            <div
              data-testid="wall-checked"
              className="font-serif text-[9rem] md:text-[13rem] leading-none tabular-nums text-emerald-300"
            >
              {board.checked}
            </div>
            <div className="text-cream/60 text-lg md:text-2xl mt-2 tabular-nums">
              / {board.total} khách · còn <span className="text-cream">{board.remaining}</span> chưa tới
            </div>
            {/* Progress bar */}
            <div className="mt-8 h-3 rounded-full bg-cream/10 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-emerald-400 to-amber-300 transition-[width] duration-700"
                style={{ width: `${percent}%` }}
              />
            </div>
          </div>

          {/* Right column stats */}
          <div className="space-y-4">
            <StatBig
              icon={<UserPlus className="w-6 h-6" />}
              label="Walk-in"
              value={board.walkins}
              tone="amber"
              testId="wall-walkins"
            />
            <StatBig
              icon={<CheckCircle2 className="w-6 h-6" />}
              label="Còn lại"
              value={board.remaining}
              tone="cream"
              testId="wall-remaining"
            />
          </div>
        </div>

        {/* Ticker — first names only */}
        <div className="mt-8">
          <div className="text-cream/40 text-[10px] uppercase tracking-[0.32em] font-semibold mb-3">
            Vừa check-in
          </div>
          <ul
            data-testid="wall-ticker"
            className="flex flex-wrap gap-3"
          >
            {board.ticker.length === 0 ? (
              <li className="text-cream/40 text-lg font-serif italic">Sẵn sàng đón khách đầu tiên…</li>
            ) : (
              board.ticker.map((t) => (
                <li
                  key={t.id}
                  data-testid={`wall-ticker-${t.id}`}
                  className={`flex items-center gap-2 rounded-2xl px-4 py-2.5 border transition ${
                    t.is_vip
                      ? "bg-gradient-to-r from-amber-400/25 via-amber-300/15 to-amber-400/25 border-amber-300/50 shadow-[0_0_28px_-6px_rgba(251,191,36,0.55)]"
                      : "bg-white/5 border-white/10"
                  }`}
                >
                  {t.is_vip && <Star className="w-4 h-4 fill-amber-200 text-amber-200" />}
                  <span className="font-serif text-2xl text-cream tabular-nums">{t.first_name}</span>
                  {t.is_walkin && (
                    <span className="text-[9px] uppercase tracking-[0.14em] text-amber-200/80 font-bold">
                      W
                    </span>
                  )}
                  <span className="text-[11px] text-cream/40 tabular-nums">
                    {formatAgo(t.checked_in_at)}
                  </span>
                </li>
              ))
            )}
          </ul>
        </div>
      </div>

      <style>{`
        @keyframes wall-flash-anim {
          0%, 100% { box-shadow: 0 0 0 0 rgba(52, 211, 153, 0); }
          20% { box-shadow: 0 0 60px 10px rgba(52, 211, 153, 0.35); }
        }
        .wall-flash { animation: wall-flash-anim 1.4s ease-out; }
      `}</style>
    </div>
  );
}

function StatBig({ icon, label, value, tone, testId }) {
  const tones = {
    amber: "text-amber-200 border-amber-300/30",
    cream: "text-cream border-cream/20",
  };
  return (
    <div
      data-testid={testId}
      className={`rounded-3xl bg-black/25 border ${tones[tone]} px-8 py-6`}
    >
      <div className={`inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] font-semibold ${tones[tone]?.split(" ")[0]}`}>
        {icon} {label}
      </div>
      <div className="font-serif text-6xl md:text-7xl tabular-nums text-cream mt-2 leading-none">
        {value}
      </div>
    </div>
  );
}
